'use client';

import { useTranslations } from 'next-intl';
import { Eye, Pencil, QrCode, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCellValue } from '@/lib/build-dynamic-form-columns';
import { PermissionGate } from '@/features/auth';
import type { DynamicFormField } from '@/features/dynamic-forms';
import type { Registration } from '../types';

interface RegistrationCardProps {
  registration: Registration;
  dynamicFormFields?: DynamicFormField[];
  onEdit: (item: Registration) => void;
  onDelete: (item: Registration) => void;
  onView: (item: Registration) => void;
  onQr: (item: Registration) => void;
}

const STAGE_STYLES: Record<string, string> = {
  registration:
    'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200',
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  ready_for_packing:
    'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

const PHYSICAL_STAGE_STYLES: Record<string, string> = {
  filling:
    'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200',
  shipping:
    'bg-cyan-100 text-cyan-800 hover:bg-cyan-100 dark:bg-cyan-900 dark:text-cyan-200',
  transport:
    'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200',
  internal_lab_sorting:
    'bg-violet-100 text-violet-800 hover:bg-violet-100 dark:bg-violet-900 dark:text-violet-200'
};

const STAGE_STATUS_STYLES: Record<string, string> = {
  creating:
    'bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200',
  returned_for_correction:
    'bg-rose-100 text-rose-800 hover:bg-rose-100 dark:bg-rose-900 dark:text-rose-200',
  returned_from_sticking:
    'bg-rose-100 text-rose-800 hover:bg-rose-100 dark:bg-rose-900 dark:text-rose-200'
};

export function RegistrationCard({
  registration,
  dynamicFormFields,
  onEdit,
  onDelete,
  onView,
  onQr
}: RegistrationCardProps) {
  const t = useTranslations('registration');

  const stageLabels: Record<string, string> = {
    registration: t('stageRegistration'),
    sticking: t('stageSticking'),
    review: t('stageReview'),
    ready_for_packing: t('stageReadyForPacking'),
    completed: t('stageCompleted')
  };

  const physicalStageLabels: Record<string, string> = {
    filling: t('physicalStageFilling'),
    shipping: t('physicalStageShipping'),
    transport: t('physicalStageTransport'),
    internal_lab_sorting: t('physicalStageInternalLabSorting')
  };

  const stageStatusLabels: Record<string, string> = {
    creating: t('stageStatusCreating'),
    returned_for_correction: t('stageStatusReturnedForCorrection'),
    returned_from_sticking: t('stageStatusReturnedFromSticking')
  };

  const responses = registration.form_responses as Record<
    string,
    unknown
  > | null;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top row: container number + badges */}
        <div className='mb-3 flex items-start justify-between gap-2'>
          <span className='font-mono text-sm font-bold'>
            {registration.container_number}
          </span>
          <div className='flex shrink-0 flex-wrap items-center justify-end gap-1'>
            <Badge
              variant='secondary'
              className={cn(
                'text-xs',
                STAGE_STYLES[registration.current_stage]
              )}
            >
              {stageLabels[registration.current_stage] ??
                registration.current_stage}
            </Badge>
            {registration.stage_status &&
              stageStatusLabels[registration.stage_status] && (
                <Badge
                  variant='secondary'
                  className={cn(
                    'text-xs',
                    STAGE_STATUS_STYLES[registration.stage_status]
                  )}
                >
                  {stageStatusLabels[registration.stage_status]}
                </Badge>
              )}
            {registration.physical_stage && (
              <Badge
                variant='secondary'
                className={cn(
                  'text-xs',
                  PHYSICAL_STAGE_STYLES[registration.physical_stage]
                )}
              >
                {physicalStageLabels[registration.physical_stage] ??
                  registration.physical_stage}
              </Badge>
            )}
          </div>
        </div>

        {/* Entity name */}
        {registration.entity_name_ar && (
          <p className='text-foreground mb-3 truncate text-sm font-medium'>
            {registration.entity_name_ar}
          </p>
        )}

        {/* Dynamic form fields */}
        {dynamicFormFields && dynamicFormFields.length > 0 && (
          <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
            {dynamicFormFields.slice(0, 5).map((field) => (
              <div key={field.id} className='flex items-start gap-1.5'>
                <span className='shrink-0 font-medium'>{field.label}:</span>
                <span className='truncate'>
                  {formatCellValue(responses?.[String(field.id)], field.type)}
                </span>
              </div>
            ))}
            {registration.container_type?.name && (
              <p className='truncate'>
                {t('containerType')}: {registration.container_type.name}
              </p>
            )}
          </div>
        )}

        {/* Bottom: action buttons */}
        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => onQr(registration)}
          >
            <QrCode className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => onView(registration)}
          >
            <Eye className='h-4 w-4' />
          </Button>
          <PermissionGate permissions={['registrations.edit']}>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onEdit(registration)}
            >
              <Pencil className='h-4 w-4' />
            </Button>
          </PermissionGate>
          <PermissionGate permissions={['registrations.delete']}>
            <Button
              variant='ghost'
              size='icon'
              className='text-destructive h-8 w-8'
              onClick={() => onDelete(registration)}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </PermissionGate>
        </div>
      </CardContent>
    </Card>
  );
}
